/**
 * README
 * This is an "internal" component meant to be used solely by other RC components.
 * Please don't use this directly. If it seems like you'd benefit from it please
 * reach out to #cadence for help coordinating!
 */

import React, { useLayoutEffect, useState } from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  UseDismissProps,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react';

import { RCButton, RCButtonStandardButtonProps } from '../../RCButton/RCButton';
import { RCCloseButton, RCCloseButtonProps } from '../../RCCloseButton/RCCloseButton';
import { FloatingPortalRootContext } from '../../hooks/useFloatingPortalTarget';
import { useRegisteredId } from '../../hooks/useRegisteredId';
import { MODAL_DOM_ID } from '../../../constants';

type ElementRef = React.MutableRefObject<HTMLElement | null>;

/**
 * Lets content rendered inside the modal close the modal containing it.
 *
 * Internal — consumers reach this through the hook their wrapper exports (`useRCModal`,
 * `useRCDrawer`).
 *
 * Defaults to a no-op rather than `null` so a component reused both inside and outside a modal
 * doesn't crash on the outside path.
 */
export interface ModalContextValue {
  close: () => void;
}

const RCModalBaseContext = React.createContext<ModalContextValue>({ close: () => {} });

export const useRCModalBaseContext = () => React.useContext(RCModalBaseContext);

type PartsContextValue = ReturnType<typeof useModalBase>;

const PartsContext = React.createContext<PartsContextValue | null>(null);

function usePartsContext(part: string) {
  const value = React.useContext(PartsContext);
  if (!value) throw new Error(`RCModalBase.${part} must be rendered inside RCModalBase.Root.`);
  return value;
}

export interface RCModalBaseRootProps {
  isOpen?: boolean;
  onOpenChange?: (state: boolean) => void;
  initialFocus?: 0 | ElementRef;
  role?: 'dialog' | 'alertdialog';
  /** Renders an overlay and traps focus. Turn off for a panel that leaves the page usable. */
  isModal?: boolean;
  useDismissProps?: UseDismissProps;
  children: React.ReactNode;
}

function useModalBase({
  isOpen: openProp,
  onOpenChange: setOpenProp,
  initialFocus,
  role,
  isModal,
  useDismissProps,
}: Omit<RCModalBaseRootProps, 'children'>) {
  const modalId = useId();
  const [openState, setOpenState] = useState<boolean>(openProp || false);
  const [focusRef, setFocusRef] = useState<ElementRef>(initialFocus === 0 ? null : initialFocus);
  const [shouldReturnFocus, setShouldReturnFocus] = useState<boolean>(true);
  const [hasTrigger, setHasTrigger] = useState(false);
  const [headingId, setHeadingId] = useState<string>();
  const [descriptionId, setDescriptionId] = useState<string>();

  const isControlled = openProp !== undefined && setOpenProp !== undefined;
  const open = isControlled ? openProp : openState;
  const setOpen = isControlled ? setOpenProp : setOpenState;

  const handleOpenChange = React.useCallback((state: boolean) => {
    setOpen(state);
    if (!isControlled) {
      setOpenProp?.(state);
    }
  }, [setOpen, isControlled, setOpenProp]);

  const focus = React.useMemo(() => {
    if (focusRef !== null) return focusRef;
    if (initialFocus) return initialFocus;
    return 0;
  }, [initialFocus, focusRef]);

  // Necessary to prevent Safari from focusing the <body> element on modal close when modal is externally controlled.
  // In other browsers, the trigger element receives focus on initial click and will receive focus again on modal close.
  // In all browsers, focus should return to the passed trigger if present.
  React.useEffect(() => {
    if (open) {
      const validActiveElement = document.activeElement !== document.body && document.activeElement !== null;
      setShouldReturnFocus(hasTrigger || validActiveElement);
    }
  }, [open, hasTrigger]);

  const { refs, context } = useFloating({
    open,
    onOpenChange: handleOpenChange,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useRole(context, {
      role,
    }),
    useDismiss(context, useDismissProps),
    useClick(context, {
      enabled: true,
    }),
  ]);

  const { isMounted, status } = useTransitionStatus(context);

  return {
    modalId,
    refs,
    context,
    isModal,
    isMounted,
    status,
    focus,
    shouldReturnFocus,
    getReferenceProps,
    getFloatingProps,
    handleOpenChange,
    setFocusRef,
    setHasTrigger,
    headingId,
    setHeadingId,
    descriptionId,
    setDescriptionId,
  };
}

/** Owns the open state, focus and interactions that the other parts share. */
function Root({
  isOpen = undefined,
  onOpenChange = undefined,
  initialFocus = 0,
  role = 'dialog',
  isModal = true,
  useDismissProps = {},
  children,
}: RCModalBaseRootProps) {
  const modal = useModalBase({
    isOpen,
    onOpenChange,
    initialFocus,
    role,
    isModal,
    useDismissProps,
  });
  const { handleOpenChange } = modal;

  // Built on `handleOpenChange`, not the raw `setOpen`: closing from inside the modal has to run
  // the same path as Escape or an overlay click, or an uncontrolled modal would close without ever
  // firing the consumer's `onOpenChange`.
  const closeValue = React.useMemo(
    () => ({ close: () => handleOpenChange(false) }),
    [handleOpenChange],
  );

  return (
    <PartsContext.Provider value={modal}>
      <RCModalBaseContext.Provider value={closeValue}>
        {children}
      </RCModalBaseContext.Provider>
    </PartsContext.Provider>
  );
}

/** The element that opens the modal. Clones its only child, adding the ref and interaction props. */
function Trigger({ children }: { children: JSX.Element }) {
  const { refs, getReferenceProps, setHasTrigger } = usePartsContext('Trigger');
  const ref = useMergeRefs([refs.setReference, (children as any).ref]);

  useLayoutEffect(() => {
    setHasTrigger(true);
    return () => setHasTrigger(false);
  }, [setHasTrigger]);

  return React.cloneElement(children, {
    ...getReferenceProps({ ref }),
  });
}

interface ContentProps {
  className?: string;
  position?: 'center' | 'bottom' | 'left' | 'right';
  displayStyle?: 'panel' | 'drawer';
  /** Keeps the content in the DOM while closed. */
  keepMounted?: boolean;
  /** Names the dialog by the id of an element the consumer renders. Only for when no `Heading` renders. */
  'aria-labelledby'?: string;
  children: React.ReactNode;
}

/**
 * The dialog itself, with its overlay and focus trap. It's named by a `Heading` inside it and
 * described only when a `Description` renders.
 */
function Content({
  className = undefined,
  position = 'center',
  displayStyle = 'panel',
  keepMounted = false,
  'aria-labelledby': ariaLabelledby = undefined,
  children,
}: ContentProps) {
  const {
    refs,
    context,
    isModal,
    isMounted,
    status,
    focus,
    shouldReturnFocus,
    getFloatingProps,
    headingId,
    descriptionId,
  } = usePartsContext('Content');

  // Held in state, not a ref: `FloatingPortal` waits for a `null` root and portals once it's set.
  const [floatingRoot, setFloatingRoot] = useState<HTMLDivElement | null>(null);

  const showOverlay = keepMounted ? isModal && status !== 'unmounted' : isModal && isMounted;

  if (!isMounted && !keepMounted) return null;

  return (
    <FloatingPortal id={MODAL_DOM_ID}>
      <div
        className="rc-modal-base"
        data-status={status}
      >
        {showOverlay &&
          <FloatingOverlay
            className="rc-modal-base__overlay"
            lockScroll
          />
        }
        <FloatingFocusManager
          context={context}
          order={['content']}
          initialFocus={focus}
          returnFocus={shouldReturnFocus}
          modal={isModal}
          closeOnFocusOut={false}
          disabled={!isMounted}
          getInsideElements={() => (floatingRoot ? [floatingRoot] : [])}
        >
          <div className="rc-modal-base__wrapper">
            <div
              className={classNames(
                'rc-modal-base__content',
                { 'rc-modal-base__content--bottom': position === 'bottom' },
                { 'rc-modal-base__content--left': position === 'left' },
                { 'rc-modal-base__content--right': position === 'right' },
                { 'rc-modal-base__content--drawer': displayStyle === 'drawer' },
              )}
            >
              <div
                className={className}
                {...getFloatingProps({
                  ref: refs.setFloating,
                  'aria-labelledby': ariaLabelledby ?? headingId,
                  'aria-describedby': descriptionId,
                })}
              >
                <FloatingPortalRootContext.Provider value={floatingRoot}>
                  {children}
                </FloatingPortalRootContext.Provider>
              </div>
            </div>
          </div>
        </FloatingFocusManager>
        {/* Floating surfaces opened inside the modal render here: after the wrapper so they paint
            above it, and outside the content so its transform doesn't clip them. */}
        <div
          className="rc-modal-base__floating-root"
          ref={setFloatingRoot}
        />
      </div>
    </FloatingPortal>
  );
}

/** Names the modal. */
function Heading({ className = undefined, children }: { className?: string, children: React.ReactNode }) {
  const { setHeadingId } = usePartsContext('Heading');
  const id = useId();
  useRegisteredId(id, setHeadingId);

  return <div id={id} className={className}>{children}</div>;
}

/** Describes the modal. Leave it out for structured or interactive content, which a screen reader
 * would otherwise read in full when the modal opens. */
function Description({ className = undefined, children }: { className?: string, children: React.ReactNode }) {
  const { setDescriptionId } = usePartsContext('Description');
  const id = useId();
  useRegisteredId(id, setDescriptionId);

  return <div id={id} className={className}>{children}</div>;
}

interface CloseButtonProps {
  variant?: RCCloseButtonProps['variant'];
}

const CloseButton = React.forwardRef<HTMLButtonElement, CloseButtonProps>(
  function CloseButton({
    variant = 'transparent',
  }: CloseButtonProps, ref) {
    const { handleOpenChange } = usePartsContext('CloseButton');
    return (
      <div className="rc-modal-base__close-button">
        <RCCloseButton
          onClick={() => handleOpenChange(false)}
          ref={ref}
          variant={variant}
        />
      </div>
    );
  },
);

export interface RCModalActionProps extends Omit<RCButtonStandardButtonProps, 'children'> {
  /** Overrides the default action text. */
  buttonText?: string | JSX.Element;
  /** Whether clicking this action closes the modal after its `onClick`.
   * Defaults to `true` for `secondary` (Cancel) and `false` for `primary` and `tertiary`.
   *
   * For async work, leave this off and call `close()` from `useRCModal()` / `useRCDrawer()` once the
   * work succeeds, so the modal stays open if it fails.
   */
  closeOnClick?: boolean;
  /** Defaults to `false`.
   * Optionally focus this action when the modal opens.
   * Note: when using a destructive `primary` action, the `secondary` action should get initial focus.
   */
  initialFocus?: boolean;
}

export interface RCModalActionsProps {
  /** Defaults to a `filled` button that says 'Save'.
   * Use `true` to render a button with the default properties.
   * Use an object to render a button with overrides (e.g. `{ theme: 'danger', buttonText: 'Delete' }`)
  */
  primary?: true | RCModalActionProps;
  /** Defaults to a `muted` button that says 'Cancel'.
   * Use `true` to render a button with the default properties.
   * Use an object to render a button with overrides (e.g. `{ initialFocus: true }`)
  */
  secondary?: true | RCModalActionProps;
  /** Rarely used to render an accessory action, like editing or deleting. */
  tertiary?: RCModalActionProps;
}

/** The modal's buttons. Only `secondary` closes the modal by default; see `closeOnClick`. */
function Actions({
  primary = undefined,
  secondary = undefined,
  tertiary = undefined,
}: RCModalActionsProps) {
  const { modalId, handleOpenChange, setFocusRef } = usePartsContext('Actions');

  const actionRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (actionRef.current) {
      setFocusRef(actionRef);
    }
  }, [setFocusRef, actionRef]);

  const defaultActions: { [p: string]: RCModalActionProps } = {
    primary: {
      buttonText: I18n.t('cadence.RCModal.save'),
      closeOnClick: false,
      initialFocus: false,
      variant: 'filled',
      id: `${modalId}-action-primary`,
    },
    secondary: {
      buttonText: I18n.t('cadence.RCModal.cancel'),
      closeOnClick: true,
      initialFocus: false,
      variant: 'muted',
      id: `${modalId}-action-secondary`,
    },
    tertiary: {
      closeOnClick: false,
      initialFocus: false,
      variant: 'transparent',
      id: `${modalId}-action-tertiary`,
    },
  };

  function renderAction(action: true | RCModalActionProps | undefined, defaults: RCModalActionProps) {
    if (action === undefined) return null;
    const props = action === true ? defaults : { ...defaults, ...action };
    const { closeOnClick, initialFocus, buttonText, ...buttonProps } = props;

    return (
      <div className={classNames(
        'rc-modal-base__action',
        { 'rc-modal-base__action--full-width': props.fullWidth },
      )}>
        <RCButton
          {...(buttonProps as RCButtonStandardButtonProps)}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            buttonProps.onClick?.(e);
            if (closeOnClick) handleOpenChange(false);
          }}
          ref={initialFocus ? actionRef : undefined}
        >
          {buttonText}
        </RCButton>
      </div>
    );
  }

  return (
    <div className="rc-modal-base__actions">
      {renderAction(primary, defaultActions.primary)}
      {renderAction(secondary, defaultActions.secondary)}
      {renderAction(tertiary, defaultActions.tertiary)}
    </div>
  );
}

/** _Note: This is an **internal** component. Please reach out to #cadence before using._ */
export const RCModalBase = {
  Root,
  Trigger,
  Content,
  Heading,
  Description,
  CloseButton,
  Actions,
};
